import ScrollReveal from '@/components/ui/ScrollReveal'

interface PatternDetail {
  label: string
  video?: string
}

interface Pattern {
  name: string
  description: string
  details: PatternDetail[]
}

const patterns: Pattern[] = [
  {
    name: 'Hierarchy',
    description:
      'Sliding content reveals secondary information without breaking context. Expanding panels reinforce continuity, solving complaints that state changes felt like disorienting jump cuts.',
    details: [
      { label: 'Everything is ok', video: '/videos/vodafone/pattern-hierarchy-1.mp4' },
      { label: 'Current bill panel', video: '/videos/vodafone/pattern-hierarchy-2.mp4' },
      { label: 'Tabbed content', video: '/videos/vodafone/pattern-hierarchy-3.mp4' },
      { label: 'Expanding offer panel', video: '/videos/vodafone/pattern-hierarchy-4.mp4' },
    ],
  },
  {
    name: 'Guidance',
    description:
      'Items slide in with distinct momentum, serving as visual hints for swipe gestures that usability tests revealed were being missed. Subtle cues encourage interaction with primary CTAs.',
    details: [
      { label: 'Tray swipe suggestions', video: '/videos/vodafone/pattern-guidance-1.mp4' },
      { label: 'Tobi notification', video: '/videos/vodafone/pattern-guidance-2.mp4' },
      { label: 'Tap to reveal', video: '/videos/vodafone/pattern-guidance-3.mp4' },
      { label: 'Price picker', video: '/videos/vodafone/pattern-guidance-4.mp4' },
    ],
  },
  {
    name: 'Feedback',
    description:
      'Clear, instant feedback mechanisms transform mundane functional steps into satisfying interactive moments. Tactile animations integrated with device haptics elevate basic actions.',
    details: [
      { label: 'Touch states & haptics', video: '/videos/vodafone/pattern-feedback-1.mp4' },
      { label: 'Error states', video: '/videos/vodafone/pattern-feedback-2.mp4' },
      { label: 'Skeleton load', video: '/videos/vodafone/pattern-feedback-3.mp4' },
    ],
  },
  {
    name: 'Expression',
    description:
      'Animated system icons add polish without affecting performance. High-fidelity illustrations celebrate milestones, and gamified elements turn everyday interactions into micro-games.',
    details: [
      { label: 'System icons', video: '/videos/vodafone/pattern-expression-icons-1.mp4' },
      { label: 'Hi-fi icons', video: '/videos/vodafone/pattern-expression-icons-2.mp4' },
      { label: 'Illustrations', video: '/videos/vodafone/pattern-expression-illustration.mp4' },
      { label: 'TOBi', video: '/videos/vodafone/pattern-expression-tobi.mp4' },
      { label: 'Gamification', video: '/videos/vodafone/pattern-expression-gamification-1.mp4' },
      {
        label: 'Gamification (advanced)',
        video: '/videos/vodafone/pattern-expression-gamification-2.mp4',
      },
      { label: 'Parallax scroll', video: '/videos/vodafone/pattern-expression-depth.mp4' },
    ],
  },
]

function PatternCard({ pattern }: { pattern: Pattern }) {
  const videoDetails = pattern.details.filter((d) => d.video)
  const textOnlyDetails = pattern.details.filter((d) => !d.video)

  return (
    <div className="bg-surface border border-border rounded-2xl p-6 md:p-8">
      <h3 className="text-h3 text-text-primary">{pattern.name}</h3>
      <p className="text-body-sm text-text-muted mt-3">{pattern.description}</p>

      {videoDetails.length > 0 && (
        <div className={`grid gap-4 mt-6 ${videoDetails.length >= 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {videoDetails.map((detail) => (
            <div key={detail.label}>
              <video
                className="w-full rounded-xl"
                src={detail.video}
                autoPlay
                muted
                loop
                playsInline
              />
              <p className="text-caption text-text-muted mt-2">{detail.label}</p>
            </div>
          ))}
        </div>
      )}

      {textOnlyDetails.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {textOnlyDetails.map((detail) => (
            <span
              key={detail.label}
              className="text-caption text-text-muted bg-background rounded-lg px-3 py-1"
            >
              {detail.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function VodafoneInteractionPatterns() {
  return (
    <div>
      <ScrollReveal>
        <span className="text-label text-accent block mb-1">01</span>
        <p className="text-label uppercase tracking-widest text-text-muted mb-2">Patterns</p>
        <h2 className="text-h2 text-text-primary mb-3">Motion Principles</h2>
        <p className="text-body text-text-secondary mb-8 max-w-2xl">
          Examples of Vodafone's motion principles in action, grouped into four categories:
        </p>
      </ScrollReveal>

      <div className="grid grid-cols-1 gap-8">
        {patterns.map((pattern, index) => (
          <ScrollReveal key={pattern.name} delay={index * 0.1}>
            <PatternCard pattern={pattern} />
          </ScrollReveal>
        ))}
      </div>
    </div>
  )
}
